import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmployeeProfile } from "@/components/employee/EmployeeProfile";
import { EmployeeModal } from "@/components/employee/EmployeeModal";
import { OvertimeHistory } from "@/components/employee/OvertimeHistory";
import { ChangeDepartment } from "@/components/employee/ChangeDepartment";
import { ChangeStatus } from "@/components/employee/ChangeStatus";
import { Employee } from "../hooks/useEmployeeManagement";

interface Department {
  id: string;
  name: string;
  company_id: string;
}

interface EmployeeDialogsProps {
  isProfileDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isOvertimeDialogOpen: boolean;
  isChangeDeptDialogOpen: boolean;
  isChangeStatusDialogOpen: boolean;
  selectedEmployee: Employee | null;
  departments: Department[];
  isLoadingDepartments: boolean;
  acceptedDomains: string[];
  handleProfileDialogClose: (open: boolean) => void;
  handleEditDialogClose: (open: boolean) => void;
  handleOvertimeDialogClose: (open: boolean) => void;
  handleChangeDeptDialogClose: (open: boolean) => void;
  handleChangeStatusDialogClose: (open: boolean) => void;
  saveEditedUser: (updatedEmployee: Employee) => void;
  saveDepartmentChange: (userId: string, newDepartment: string, reason: string) => void;
  saveStatusChange: (userId: string, newStatus: string, reason: string) => void;
}

export function EmployeeDialogs({
  isProfileDialogOpen,
  isEditDialogOpen,
  isOvertimeDialogOpen,
  isChangeDeptDialogOpen,
  isChangeStatusDialogOpen,
  selectedEmployee,
  departments,
  isLoadingDepartments,
  acceptedDomains,
  handleProfileDialogClose,
  handleEditDialogClose,
  handleOvertimeDialogClose,
  handleChangeDeptDialogClose,
  handleChangeStatusDialogClose,
  saveEditedUser,
  saveDepartmentChange,
  saveStatusChange
}: EmployeeDialogsProps) {
  
  return (
    <>
      {/* Employee Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={handleProfileDialogClose}>
        <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Profile</DialogTitle>
            <DialogDescription className="sr-only">View employee details and information</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <EmployeeProfile 
              employee={{
                ...selectedEmployee,
                department: Array.isArray(selectedEmployee.department) 
                  ? selectedEmployee.department 
                  : (selectedEmployee.department ? [selectedEmployee.department] : [])
              }} 
              onClose={() => handleProfileDialogClose(false)} 
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Employee Dialog */}
      {selectedEmployee && (
        <EmployeeModal
          mode="edit"
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            // Prevent opening if departments aren't loaded
            if (open && (isLoadingDepartments || departments.length === 0)) {
              return;
            }
            handleEditDialogClose(open);
          }}
          employee={selectedEmployee}
          onSave={saveEditedUser}
          departments={departments}
          isLoadingDepartments={isLoadingDepartments}
          acceptedDomains={acceptedDomains}
        />
      )}
      
      {/* Overtime History Dialog */}
      <Dialog open={isOvertimeDialogOpen} onOpenChange={handleOvertimeDialogClose}>
        <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Overtime History</DialogTitle>
            <DialogDescription className="sr-only">View employee's overtime history</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <OvertimeHistory 
              employee={selectedEmployee}
              onClose={() => handleOvertimeDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Department Dialog */}
      <Dialog open={isChangeDeptDialogOpen} onOpenChange={handleChangeDeptDialogClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Department</DialogTitle>
            <DialogDescription className="sr-only">Change employee's department</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <ChangeDepartment 
              employee={selectedEmployee}
              onSave={saveDepartmentChange}
              onCancel={() => handleChangeDeptDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Status Dialog */}
      <Dialog open={isChangeStatusDialogOpen} onOpenChange={handleChangeStatusDialogClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription className="sr-only">Change employee's status</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <ChangeStatus 
              employee={selectedEmployee}
              onSave={saveStatusChange}
              onCancel={() => handleChangeStatusDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
